<template>
  <div class="ma-root">

    <div class="sp-header">
      <div>
        <h1 class="sp-title">Manage Account</h1>
        <p class="sp-subtitle">Cancel your subscription or permanently close your account</p>
      </div>
    </div>

    <div v-if="loading" class="sp-spinner-wrap">
      <div class="sp-spinner"></div>
    </div>

    <template v-else>

      <!-- Current plan brief -->
      <div class="ma-plan-card">
        <div class="ma-plan-left">
          <span class="ma-plan-icon">{{ PLAN_ICONS[currentPlan] ?? '🌱' }}</span>
          <div>
            <div class="ma-plan-name">{{ planName }} Plan</div>
            <div class="ma-plan-detail">{{ renewsLabel }}</div>
          </div>
        </div>
        <RouterLink v-if="!isAccountClosureScheduled && !hasScheduledDowngrade && !isCancellingActive" to="/parent/subscription" class="ma-plan-link">Change plan →</RouterLink>
      </div>

      <!-- Account closure scheduled -->
      <div v-if="isAccountClosureScheduled" class="sp-cancel-section sp-cancel-section-red ma-section">
        <div class="sp-cancel-header">Account scheduled for closure</div>
        <p class="sp-cancel-desc">
          Your account and all child data will be permanently archived on <strong>{{ closureDate }}</strong>.
          You will no longer be able to log in after that date.
        </p>
        <button class="sp-cancel-link" @click="confirmingCancelClosure = true">
          Cancel account closure
        </button>
      </div>

      <!-- Subscription cancelling (already cancelled, still in paid period) -->
      <div v-else-if="isCancellingActive" class="sp-cancel-section ma-section">
        <div class="sp-cancel-header">Subscription cancelling</div>
        <p class="sp-cancel-desc">
          Your subscription has been cancelled. You still have
          <strong>{{ planName }}</strong> access until <strong>{{ formattedRenewsAt }}</strong>.
        </p>
        <button class="sp-cancel-link sp-cancel-link-reactivate" @click="confirmingReactivate = true">
          Reactivate subscription
        </button>
      </div>

      <!-- Active paid plan: cancel + close options -->
      <template v-else-if="showActions">
        <!-- Cancel subscription -->
        <div class="sp-cancel-section ma-section">
          <div class="sp-cancel-header">Cancel subscription</div>
          <p class="sp-cancel-desc">
            Your {{ planName }} features will stay active until <strong>{{ formattedRenewsAt }}</strong>.
            After that your account moves to the Free plan automatically.
          </p>
          <button class="sp-cancel-link" @click="confirmingDowngrade = true">Cancel my subscription</button>
        </div>

        <!-- Close account -->
        <div class="sp-cancel-section sp-cancel-section-red ma-section">
          <div class="sp-cancel-header">Close account</div>
          <p class="sp-cancel-desc">
            Permanently close your account at the end of the billing period (<strong>{{ formattedRenewsAt }}</strong>).
            All child profiles and progress data will be archived and you will no longer be able to log in.
          </p>
          <button class="sp-cancel-link" @click="confirmingAccountClosure = true">Close my account permanently</button>
        </div>
      </template>

      <!-- Free plan: nothing to manage here -->
      <div v-else class="ma-empty">
        <p>You're on the Free plan. <RouterLink to="/parent/subscription" class="ma-empty-link">Upgrade your plan</RouterLink> to access premium features.</p>
      </div>

      <div v-if="actionError" class="sp-error-banner ma-error">{{ actionError }}</div>

    </template>
  </div>

  <!-- ── CANCEL CONFIRMATION MODAL ── -->
  <Teleport to="body">
    <div v-if="confirmingDowngrade" class="sp-modal-overlay" @click.self="confirmingDowngrade = false">
      <div class="sp-modal">
        <div class="sp-modal-icon">⚠️</div>
        <h2 class="sp-modal-title">Cancel subscription?</h2>
        <p class="sp-modal-body">
          Your <strong>{{ planName }}</strong> features will stay active until
          <strong>{{ formattedRenewsAt }}</strong>. After that, all child profiles will be archived
          and your account moves to the Free plan:
        </p>
        <ul class="sp-modal-children">
          <li v-for="child in studentStore.students" :key="child.id" class="sp-modal-child">
            <span class="sp-modal-child-avatar">{{ child.avatar || '🧒' }}</span>
            {{ child.name }}
          </li>
        </ul>
        <p class="sp-modal-warning">
          All progress, streaks, and mastery data will be archived when your plan expires.
        </p>
        <p class="sp-modal-hook">
          Losing their progress is hard to reverse — consider staying on your current plan to preserve everything they've worked for.
        </p>
        <div class="sp-modal-actions">
          <button class="sp-btn sp-btn-downgrade-confirm" :disabled="actionLoading" @click="doCancel">
            {{ actionLoading ? 'Cancelling…' : 'Yes, cancel subscription' }}
          </button>
          <button class="sp-btn sp-btn-cancel-confirm" :disabled="actionLoading" @click="confirmingDowngrade = false">
            Keep my plan
          </button>
        </div>
        <div v-if="actionError" class="sp-modal-error">{{ actionError }}</div>
      </div>
    </div>
  </Teleport>

  <!-- ── CLOSE ACCOUNT CONFIRMATION MODAL ── -->
  <Teleport to="body">
    <div v-if="confirmingAccountClosure" class="sp-modal-overlay" @click.self="confirmingAccountClosure = false">
      <div class="sp-modal">
        <div class="sp-modal-icon">🗑️</div>
        <h2 class="sp-modal-title">Close account permanently?</h2>
        <p class="sp-modal-body">
          Your <strong>{{ planName }}</strong> access continues until
          <strong>{{ formattedRenewsAt }}</strong>. After that, your account and all child profiles
          will be <strong>permanently archived</strong> and you won't be able to log in.
        </p>
        <ul class="sp-modal-children">
          <li v-for="child in studentStore.students" :key="child.id" class="sp-modal-child">
            <span class="sp-modal-child-avatar">{{ child.avatar || '🧒' }}</span>
            {{ child.name }}
          </li>
        </ul>
        <p class="sp-modal-warning">
          All progress, streaks, and mastery data will be permanently archived. This cannot be undone after the billing period ends.
        </p>
        <div class="sp-modal-actions">
          <button class="sp-btn sp-btn-downgrade-confirm" :disabled="actionLoading" @click="doCloseAccount">
            {{ actionLoading ? 'Scheduling…' : 'Yes, close my account' }}
          </button>
          <button class="sp-btn sp-btn-cancel-confirm" :disabled="actionLoading" @click="confirmingAccountClosure = false">
            Keep my account
          </button>
        </div>
        <div v-if="actionError" class="sp-modal-error">{{ actionError }}</div>
      </div>
    </div>
  </Teleport>

  <!-- ── REACTIVATE CONFIRMATION MODAL ── -->
  <Teleport to="body">
    <div v-if="confirmingReactivate" class="sp-modal-overlay" @click.self="confirmingReactivate = false">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">🎊</span>
          <span style="animation-delay:0.15s">💛</span>
          <span style="animation-delay:0.3s">🎊</span>
        </div>
        <div class="sp-umodal-badge">✨ Don't leave — you're amazing!</div>
        <h2 class="sp-umodal-title">Brilliant — let's keep this going! 🎉</h2>
        <p class="sp-umodal-sub">
          Every practice session is one step closer to that dream Grammar School place.
          Your <strong>{{ planName }}</strong> subscription will stay active — every streak,
          every mastery band, every bit of your child's hard work stays exactly where it belongs.
        </p>
        <div class="sp-modal-actions" style="margin-top:24px">
          <button class="sp-btn sp-btn-upgrade-confirm sp-btn-reactivate-confirm" :disabled="actionLoading" @click="doReactivate">
            {{ actionLoading ? 'Reactivating…' : 'Yes, keep my subscription →' }}
          </button>
          <button class="sp-btn sp-btn-cancel-confirm" :disabled="actionLoading" @click="confirmingReactivate = false">Not now</button>
        </div>
        <div v-if="actionError" class="sp-modal-error">{{ actionError }}</div>
      </div>
    </div>
  </Teleport>

  <!-- ── REACTIVATE SUCCESS MODAL ── -->
  <Teleport to="body">
    <div v-if="reactivateSuccess" class="sp-modal-overlay">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">🎉</span>
          <span style="animation-delay:0.15s">🚀</span>
          <span style="animation-delay:0.3s">🎉</span>
        </div>
        <div class="sp-umodal-badge sp-umodal-badge-success">{{ PLAN_ICONS[currentPlan] ?? '⭐' }} {{ planName }} — Active</div>
        <h2 class="sp-umodal-title">Yes! You're back in action! 🚀</h2>
        <p class="sp-umodal-sub">
          Your <strong>{{ planName }}</strong> subscription is live again.
          Every session, every streak and every mastery milestone is exactly where you left it.
          Now let's get back to it — that Grammar School place won't win itself!
        </p>
        <div class="sp-modal-actions" style="margin-top:24px">
          <button class="sp-btn sp-btn-upgrade-confirm" @click="reactivateSuccess = false">
            Let's keep learning →
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ── CANCEL SUBSCRIPTION SUCCESS MODAL ── -->
  <Teleport to="body">
    <div v-if="cancelSuccess" class="sp-modal-overlay">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">💛</span>
          <span style="animation-delay:0.15s">😔</span>
          <span style="animation-delay:0.3s">💛</span>
        </div>
        <div class="sp-umodal-badge" style="background:#fef3c7;color:#92400e;border-color:#fcd34d">Subscription cancelled</div>
        <h2 class="sp-umodal-title">We'll miss you!</h2>
        <p class="sp-umodal-sub">
          Your <strong>{{ planName }}</strong> features stay active until <strong>{{ formattedRenewsAt }}</strong>.
          Your children's progress and mastery data will always be here — we hope to see you back on the journey soon.
        </p>
        <div class="sp-modal-actions" style="margin-top:24px">
          <button class="sp-btn sp-btn-upgrade-confirm" style="background:#64748b" @click="cancelSuccess = false">
            Got it
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ── CLOSE ACCOUNT SCHEDULED SUCCESS MODAL ── -->
  <Teleport to="body">
    <div v-if="closureScheduledSuccess" class="sp-modal-overlay">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">💛</span>
          <span style="animation-delay:0.15s">🕊️</span>
          <span style="animation-delay:0.3s">💛</span>
        </div>
        <div class="sp-umodal-badge" style="background:#fee2e2;color:#b91c1c;border-color:#fca5a5">Closure scheduled</div>
        <h2 class="sp-umodal-title">We're sad to see you go</h2>
        <p class="sp-umodal-sub">
          Your account will be permanently archived on <strong>{{ closureDate }}</strong>.
          Until then you still have full <strong>{{ planName }}</strong> access.
          You can cancel this at any time from <em>Manage Account</em> — we hope you change your mind!
        </p>
        <div class="sp-modal-actions" style="margin-top:24px">
          <button class="sp-btn sp-btn-upgrade-confirm" style="background:#64748b" @click="closureScheduledSuccess = false">
            Got it
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ── CANCEL CLOSURE CONFIRMATION MODAL ── -->
  <Teleport to="body">
    <div v-if="confirmingCancelClosure" class="sp-modal-overlay" @click.self="confirmingCancelClosure = false">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">🎊</span>
          <span style="animation-delay:0.15s">💛</span>
          <span style="animation-delay:0.3s">🎊</span>
        </div>
        <div class="sp-umodal-badge">🙌 So glad you reconsidered!</div>
        <h2 class="sp-umodal-title">Wonderful — we're so happy you're staying! 🎉</h2>
        <p class="sp-umodal-sub">
          Your children's hard-earned progress means everything — and we'd hate to see it go.
          Cancel the closure and your <strong>{{ planName }}</strong> subscription continues
          in full, with every streak, mastery band and progress record completely safe.
        </p>
        <div class="sp-modal-actions" style="margin-top:24px">
          <button class="sp-btn sp-btn-upgrade-confirm sp-btn-reactivate-confirm" :disabled="actionLoading" @click="doCancelAccountClosure">
            {{ actionLoading ? 'Cancelling closure…' : 'Yes, keep my account →' }}
          </button>
          <button class="sp-btn sp-btn-cancel-confirm" :disabled="actionLoading" @click="confirmingCancelClosure = false">Not now</button>
        </div>
        <div v-if="actionError" class="sp-modal-error">{{ actionError }}</div>
      </div>
    </div>
  </Teleport>

  <!-- ── CANCEL CLOSURE SUCCESS MODAL ── -->
  <Teleport to="body">
    <div v-if="closureSuccess" class="sp-modal-overlay">
      <div class="sp-modal sp-umodal">
        <div class="sp-umodal-confetti">
          <span style="animation-delay:0s">🎉</span>
          <span style="animation-delay:0.15s">🌟</span>
          <span style="animation-delay:0.3s">🎉</span>
        </div>
        <div class="sp-umodal-badge sp-umodal-badge-success">{{ PLAN_ICONS[currentPlan] ?? '⭐' }} {{ planName }} — Active</div>
        <h2 class="sp-umodal-title">Amazing — so glad you're with us! 🌟</h2>
        <p class="sp-umodal-sub">
          Account closure cancelled! Your <strong>{{ planName }}</strong> subscription is fully active.
          Your children can dive straight back into their learning journey — every question answered,
          every streak built and every mastery band earned is still there waiting for them!
        </p>
        <div class="sp-modal-actions" style="margin-top:24px">
          <button class="sp-btn sp-btn-upgrade-confirm" @click="closureSuccess = false">
            Back to learning →
          </button>
        </div>
      </div>
    </div>
  </Teleport>

</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { useStudentStore } from '@/stores/studentStore';
import { useAuth } from '@/composables/useAuth';
import { accountApi } from '@/services/accountApi';
import * as authService from '@/services/authService';
import { PLAN_ICONS } from '@/services/planService';
import { useAnalytics } from '@/composables/useAnalytics';

const userStore     = useUserStore();
const studentStore  = useStudentStore();
const { session }   = useAuth();
const { track }     = useAnalytics();

const PLAN_LABELS = { free: 'Free', pro: 'Pro', pro_plus: 'Pro Plus', family: 'Family' };

// ── State ─────────────────────────────────────────────────────────────────────
const loading              = ref(true);
const actionLoading        = ref(false);
const actionError          = ref('');
const confirmingDowngrade  = ref(false);
const confirmingReactivate = ref(false);
const confirmingAccountClosure = ref(false);
const confirmingCancelClosure    = ref(false);
const closureSuccess             = ref(false);
const cancelSuccess              = ref(false);
const closureScheduledSuccess    = ref(false);
const reactivateSuccess          = ref(false);

const subscription = ref({
  plan: session.value?.plan || 'free',
  status: 'active',
  renewsAt: null,
  cancelledAt: null,
});

// ── Derived ───────────────────────────────────────────────────────────────────
const currentPlan = computed(() => subscription.value.effectivePlan || subscription.value.plan || 'free');
const planName    = computed(() => PLAN_LABELS[currentPlan.value] ?? currentPlan.value);

const isCancellingActive = computed(() =>
  subscription.value.status === 'cancelled' && currentPlan.value !== 'free'
);
const isAccountClosureScheduled = computed(() => !!subscription.value.scheduledClosure);
const hasScheduledDowngrade     = computed(() => !!subscription.value.scheduledDowngrade);

const formattedRenewsAt = computed(() => {
  const d = subscription.value.renewsAt;
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
});
const closureDate = computed(() => {
  const d = subscription.value.scheduledClosure?.effectiveAt;
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
});
const renewsLabel = computed(() => {
  if (isAccountClosureScheduled.value) return `Closing · ${closureDate.value}`;
  if (isCancellingActive.value)        return `Active until ${formattedRenewsAt.value}`;
  if (subscription.value.renewsAt)     return `Renews ${formattedRenewsAt.value}`;
  return 'Free forever';
});

// Show cancel/close options only for an active paid plan with no pending state changes
const showActions = computed(() =>
  currentPlan.value !== 'free' &&
  !isCancellingActive.value &&
  !isAccountClosureScheduled.value &&
  !!subscription.value.renewsAt
);

// ── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(async () => {
  const uid = session.value?.uid;
  if (!uid) { loading.value = false; return; }
  try {
    const [{ account }] = await Promise.all([
      accountApi.getAccount(uid),
      studentStore.loadChildren(uid),
    ]);
    if (account?.subscription) subscription.value = account.subscription;
  } catch (e) {
    console.warn('[ManageAccountPage] Failed to load:', e);
  } finally {
    loading.value = false;
  }
});

// ── Actions ───────────────────────────────────────────────────────────────────
async function doCancel() {
  actionError.value   = '';
  actionLoading.value = true;
  try {
    const uid = session.value?.uid;
    await accountApi.cancelSubscription(uid);
    const { account } = await accountApi.getAccount(uid);
    subscription.value = account.subscription;
    authService.setSubscription(uid, account.subscription);
    userStore.session = authService.getSession();
    confirmingDowngrade.value = false;
    cancelSuccess.value = true;
    track('subscription_cancelled', { plan: currentPlan.value });
  } catch (e) {
    actionError.value = e.data?.error || 'Cancellation failed. Please try again.';
  } finally {
    actionLoading.value = false;
  }
}

async function doReactivate() {
  actionError.value   = '';
  actionLoading.value = true;
  try {
    const uid = session.value?.uid;
    await accountApi.reactivateSubscription(uid);
    const { account } = await accountApi.getAccount(uid);
    subscription.value = account.subscription;
    authService.setSubscription(uid, account.subscription);
    userStore.session = authService.getSession();
    confirmingReactivate.value = false;
    reactivateSuccess.value = true;
    track('subscription_reactivated', { plan: currentPlan.value });
  } catch (e) {
    actionError.value = e.data?.error || 'Reactivation failed. Please try again.';
  } finally {
    actionLoading.value = false;
  }
}

async function doCloseAccount() {
  actionError.value   = '';
  actionLoading.value = true;
  try {
    const uid = session.value?.uid;
    await accountApi.scheduleAccountClosure(uid);
    const { account } = await accountApi.getAccount(uid);
    subscription.value = account.subscription;
    authService.setSubscription(uid, account.subscription);
    userStore.session = authService.getSession();
    await studentStore.loadChildren(uid);
    confirmingAccountClosure.value = false;
    closureScheduledSuccess.value = true;
    track('account_closure_scheduled', { plan: currentPlan.value });
  } catch (e) {
    actionError.value = e.data?.error || 'Failed to schedule account closure. Please try again.';
  } finally {
    actionLoading.value = false;
  }
}

async function doCancelAccountClosure() {
  actionError.value   = '';
  actionLoading.value = true;
  try {
    const uid = session.value?.uid;
    await accountApi.cancelAccountClosure(uid);
    const { account } = await accountApi.getAccount(uid);
    subscription.value = account.subscription;
    authService.setSubscription(uid, account.subscription);
    userStore.session = authService.getSession();
    await studentStore.loadChildren(uid);
    confirmingCancelClosure.value = false;
    closureSuccess.value = true;
    track('account_closure_cancelled', { plan: currentPlan.value });
  } catch (e) {
    actionError.value = e.data?.error || 'Failed to cancel account closure. Please try again.';
  } finally {
    actionLoading.value = false;
  }
}
</script>

<style>
.ma-root {
  width: 100%;
  padding-bottom: 48px;
}
.ma-plan-card {
  background: white;
  border-radius: 14px;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.ma-plan-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ma-plan-icon { font-size: 24px; }
.ma-plan-name {
  font-size: 15px;
  font-weight: 700;
  color: #1A2332;
}
.ma-plan-detail {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}
.ma-plan-link {
  font-size: 13px;
  font-weight: 600;
  color: #6366f1;
  text-decoration: none;
}
.ma-plan-link:hover { text-decoration: underline; }
.ma-section { margin-top: 24px; }
.ma-error   { margin-top: 16px; }
.ma-empty {
  margin-top: 24px;
  font-size: 14px;
  color: #64748b;
}
.ma-empty-link { color: #6366f1; }
</style>
